import { PAGE_SIZES, pageCount, pageNumbers, pageRange, type Page } from '@cyberscore/contracts';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Rows3,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { ListState } from '../lib/list';
import { navigate } from '../lib/router';

/**
 * One way to show any list (task 60): search, filters, sort, cards or a table, and pages. It only draws
 * and reports changes; the list's state lives in the address (useListState) and the server does the work.
 */

export interface Column<T> {
  key: string;
  label: ReactNode;
  /** The server's sort name, when this column can be sorted by. */
  sort?: string;
  render: (row: T) => ReactNode;
  align?: 'right' | 'center';
  /** Hidden on narrow screens. */
  wide?: boolean;
}

export interface FilterDef {
  key: string;
  label: string;
  options: readonly (readonly [value: string, label: string])[];
}

export interface SortDef {
  value: string;
  label: string;
  /** The direction it starts in when picked. */
  dir?: 'asc' | 'desc';
}

interface Props<T> {
  list: { state: ListState; set: (p: Partial<ListState>) => void; clear: () => void; filtered: boolean };
  query: {
    data?: Page<T>;
    isPending: boolean;
    isError: boolean;
    error: Error | null;
    isPlaceholderData?: boolean;
  };
  rowKey: (row: T) => string;
  columns: Column<T>[];
  /** Cards: when given, the list can switch between cards and the table. */
  card?: (row: T) => ReactNode;
  /** Cards one under another, for cards with a lot in them. */
  wideCards?: boolean;
  rowHref?: (row: T) => string;
  /** Instead of a link, a click on a row does this (e.g. opens a quick view). */
  onRow?: (row: T) => void;
  filters?: FilterDef[];
  sorts: SortDef[];
  searchLabel: string;
  /** What "nothing yet" says, before any search or filter. */
  empty: ReactNode;
  /** Extra controls at the end of the toolbar. */
  actions?: ReactNode;
  /** Thing being counted, e.g. "clients". */
  noun?: string;
  /**
   * Filters and sort behind one "Filter and sort" button (task 109, decision 0074), for a list whose
   * ready-made views cover most needs. Active filters still show as chips.
   */
  foldFilters?: boolean;
}

export function DataView<T>(p: Props<T>) {
  const { state, set, clear, filtered } = p.list;
  const page = p.query.data;
  const cards = !!p.card && state.view === 'cards';
  const sortLabel = (v: string) => p.sorts.find((s) => s.value === v)?.label ?? v;
  const [unfolded, setUnfolded] = useState(false);
  const active = p.filters?.filter((f) => state.filters[f.key]).length ?? 0;
  const showFilters = !p.foldFilters || unfolded;

  return (
    <section className="card dv" aria-busy={p.query.isPending || p.query.isPlaceholderData}>
      <div className="dv-bar">
        <SearchBox value={state.q} label={p.searchLabel} onChange={(q) => set({ q })} />
        {p.foldFilters && (
          <button type="button" className="btn" aria-expanded={unfolded} onClick={() => setUnfolded(!unfolded)}>
            <SlidersHorizontal size={15} /> Filter and sort{active ? ` (${active})` : ''}
          </button>
        )}
        {showFilters && p.filters?.map((f) => (
          <select key={f.key} className="input dv-filter" aria-label={f.label} value={state.filters[f.key] ?? ''} onChange={(e) => set({ filters: { [f.key]: e.target.value } } as Partial<ListState>)}>
            <option value="">{`All ${f.label.toLowerCase()}`}</option>
            {f.options.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        ))}
        {showFilters && <div className="dv-sort">
          <select className="input" aria-label="Sort by" value={state.sort} onChange={(e) => { const s = p.sorts.find((x) => x.value === e.target.value); set({ sort: e.target.value, dir: s?.dir ?? state.dir }); }}>
            {p.sorts.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button type="button" className="btn btn-icon" aria-label={state.dir === 'asc' ? 'Ascending: switch to descending' : 'Descending: switch to ascending'} title={state.dir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => set({ dir: state.dir === 'asc' ? 'desc' : 'asc' })}>
            {state.dir === 'asc' ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
          </button>
        </div>}
        {p.card && (
          <div className="seg" role="group" aria-label="View">
            <button type="button" aria-pressed={cards} onClick={() => set({ view: 'cards', page: state.page })} title="Cards"><LayoutGrid size={15} /> <span>Cards</span></button>
            <button type="button" aria-pressed={!cards} onClick={() => set({ view: 'table', page: state.page })} title="Table"><Rows3 size={15} /> <span>Table</span></button>
          </div>
        )}
        {p.actions}
      </div>

      {filtered && (
        <div className="dv-active">
          {state.q.trim() && <Chip onClear={() => set({ q: '' })}>“{state.q.trim()}”</Chip>}
          {p.filters?.map((f) => {
            const v = state.filters[f.key];
            const label = f.options.find(([x]) => x === v)?.[1];
            return v ? <Chip key={f.key} onClear={() => set({ filters: { [f.key]: '' } } as Partial<ListState>)}>{f.label}: {label ?? v}</Chip> : null;
          })}
          <button type="button" className="link-btn small" onClick={clear}>Clear all</button>
        </div>
      )}

      {p.query.isError && !page ? (
        <div className="empty"><strong>Could not load this list</strong>{p.query.error?.message}</div>
      ) : !page ? (
        <Skeleton cards={cards} />
      ) : !page.items.length ? (
        <div className="empty">{filtered ? <><strong>Nothing matches</strong>Try another search, or clear the filters.</> : p.empty}</div>
      ) : cards ? (
        <div className={`dv-cards${p.wideCards ? ' wide' : ''}`}>
          {page.items.map((row) => (
            <div key={p.rowKey(row)} className="dv-card-wrap">{p.card!(row)}</div>
          ))}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid dv-table">
            <thead>
              <tr>
                {p.columns.map((c) => {
                  const on = !!c.sort && state.sort === c.sort;
                  return (
                    <th key={c.key} className={[c.align ?? '', c.wide ? 'wide' : ''].join(' ').trim() || undefined} aria-sort={on ? (state.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                      {c.sort ? (
                        <button type="button" className={`th-sort${on ? ' on' : ''}`} onClick={() => set({ sort: c.sort!, dir: on ? (state.dir === 'asc' ? 'desc' : 'asc') : (p.sorts.find((s) => s.value === c.sort)?.dir ?? 'desc') })} title={`Sort by ${sortLabel(c.sort).toLowerCase()}`}>
                          {c.label}
                          {on ? state.dir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} /> : null}
                        </button>
                      ) : (
                        c.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {page.items.map((row) => {
                const to = p.rowHref?.(row);
                const go = p.onRow ? () => p.onRow!(row) : to ? () => navigate(to) : null;
                return (
                  <tr key={p.rowKey(row)} className={go ? 'clickable' : undefined} onClick={go ? (e) => { if (!(e.target as HTMLElement).closest('a,button,input,select')) go(); } : undefined}>
                    {p.columns.map((c) => (
                      <td key={c.key} className={[c.align ?? '', c.wide ? 'wide' : ''].join(' ').trim() || undefined}>{c.render(row)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {page && page.total > 0 && <Pager page={page} noun={p.noun} onPage={(n) => set({ page: n })} onSize={(n) => set({ page_size: n })} />}
    </section>
  ); // prettier-ignore
}

function SearchBox({
  value,
  label,
  onChange,
}: {
  value: string;
  label: string;
  onChange: (v: string) => void;
}) {
  const [text, setText] = useState(value);
  const [seen, setSeen] = useState(value);
  // The address changed it (back, a cleared chip): show that.
  if (value !== seen) {
    setSeen(value);
    setText(value);
  }
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), 300);
    return () => clearTimeout(t);
  }, [text, value, onChange]);
  return (
    <label className="search dv-search">
      <Search size={15} aria-hidden="true" />
      <input className="input" type="search" placeholder={label} aria-label={label} value={text} maxLength={100} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onChange(text)} />
    </label>
  ); // prettier-ignore
}

function Chip({ children, onClear }: { children: ReactNode; onClear: () => void }) {
  return (
    <span className="dv-chip">
      {children}
      <button type="button" aria-label="Remove" onClick={onClear}>
        <X size={12} />
      </button>
    </span>
  );
}

function Pager({
  page,
  noun,
  onPage,
  onSize,
}: {
  page: Page<unknown>;
  noun?: string;
  onPage: (n: number) => void;
  onSize: (n: number) => void;
}) {
  const pages = pageCount(page);
  return (
    <nav className="dv-foot" aria-label="Pages">
      <span className="muted small">{pageRange(page)}{noun ? ` ${noun}` : ''}</span>
      <label className="muted small dv-size">
        Show
        <select className="input" value={page.page_size} onChange={(e) => onSize(Number(e.target.value))} aria-label="Rows per page">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      {pages > 1 && (
        <div className="dv-pages">
          <button type="button" className="btn btn-sm" disabled={page.page <= 1} onClick={() => onPage(page.page - 1)} aria-label="Previous page"><ChevronLeft size={15} /></button>
          {pageNumbers(Math.min(page.page, pages), pages).map((n, i) =>
            n === null ? <span key={`gap${i}`} className="muted">…</span> : (
              <button key={n} type="button" className={`btn btn-sm${n === page.page ? ' on' : ''}`} aria-current={n === page.page ? 'page' : undefined} onClick={() => onPage(n)}>{n}</button>
            ),
          )}
          <button type="button" className="btn btn-sm" disabled={page.page >= pages} onClick={() => onPage(page.page + 1)} aria-label="Next page"><ChevronRight size={15} /></button>
        </div>
      )}
    </nav>
  ); // prettier-ignore
}

function Skeleton({ cards }: { cards: boolean }) {
  return cards ? (
    <div className="dv-cards">{Array.from({ length: 6 }, (_, i) => <div key={i} className="dv-card-wrap"><div className="dv-skel card-skel" /></div>)}</div>
  ) : (
    <div className="dv-rows">{Array.from({ length: 6 }, (_, i) => <div key={i} className="dv-skel row-skel" />)}</div>
  ); // prettier-ignore
}

/** A meter for progress or confidence, 0–1. */
export function Meter({ value, tone }: { value: number; tone?: 'ok' | 'warn' | 'bad' }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <span className="meter-cell">
      <span className="meter thin"><span className={`fill ${tone ?? (pct >= 60 ? 'ok' : 'warn')}`} style={{ width: `${pct}%` }} /></span>
      <span className="num small">{pct}%</span>
    </span>
  ); // prettier-ignore
}
